import classNames from 'classnames';
import _debounce from 'lodash/debounce';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import {StrictMode, Suspense, lazy, useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Navigate, Route, Routes, useNavigate, useLocation, useNavigationType, type Location} from 'react-router-dom';

// Components
import Loader from '../components/common/Loader';
import HeaderDsp from '../components/header/HeaderDsp';
import HeaderDwa from '../components/header/HeaderDwa';
import {LoggedOut} from '../components/LoggedOut';
import ErrorBoundary from './ErrorBoundary';
import FcraModal from './fcra/FcraModal';
import {IntlProvider} from './IntlProvider';

// Actions
import {alertDetailMobileMemberChat} from '../actions/alertDetail';
import {isMobileApp, loginRefresh, refreshToken, whiteLabelClient} from '../actions/auth';
import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEY_MEMBER_DATA} from '../customHooks/useMemberData';
import {setModalQuery, setModalSizeSpec} from '../actions/modal';
import {setWindowScrolledEvent} from '../actions/scroll';

// Reducers
import {RootState} from '../reducers/rootReducer';

// Hooks
import usePrevious from '../customHooks/usePrevious';
import {useUserActivity} from '../customHooks/useUserActivity';
import {useFastLinkToken} from '../customHooks/useFastLinkToken';
import {useTransactions} from '../customHooks/useTransactions';
import {useAlerts} from '../customHooks/useAlerts';
import {useAnalytics} from '../customHooks/useAnalytics';
import {useCreditData} from '../customHooks/useCreditData';
import {useQuebecOptIn} from '../customHooks/useQuebecOptIn';
import {
  isPrivacyMonitorLicenseValid,
  invalidatePrivacyMonitorQueries,
  usePrivacyMonitorLicense,
} from '../customHooks/usePrivacyMonitor';
import {useFcraConsent} from '../customHooks/useFcraConsent';
import {useFinancialOffers} from '../customHooks/useFinancialOffers';

// Helpers
import {authWithNslToken, checkUser, startNslHandshake, startSso, startTokenExchange} from '../helpers/apiCalls';
import errorCodes from '../helpers/errorCodes';
import {setHeaderAccessToken} from '../helpers/fetchRest';
import {
  rejectPendingNslTokenRefresh,
  resolvePendingNslTokenRefresh,
  setNslToken,
  stopNslTokenRefresh,
  updateNslToken,
} from '../helpers/token';
import {isMobileInFlowFinancialSurfacePath, shouldUseDspUi, shouldUseDwmUi} from '../helpers/layout';
import log from '../helpers/log';
import {
  loadCssUrls,
  loadPartnerTheme,
  overrideConfigs,
  getCustomPartnerConfig,
  isWhiteLabelClientCustomConfig,
  hasFixedMobileMenu,
} from '../helpers/partnerUtils';
import {hasCreditAccountDeepLink} from '../helpers/creditDeepLink';
import {initTrackingValues} from '../helpers/tracking';
import utils from '../helpers/utils';
import {syncReimagineTheme} from '../helpers/themeUtils';
import {
  buildSmmOAuthStubNavigateTarget,
  decodeHashPathForRouting,
  isSocialMediaOAuthRedirectStubPathname,
  normalizeHashPathnameForRouting,
} from '../helpers/decodeHashPathForRouting';
import {
  clearSmmPendingOAuthReturn,
  extractSearchFromWindowHash,
  isOAuthStubInWindowHash,
  resolveSmmOAuthCancelReturnTarget,
  resolveSmmOAuthStubNavigateTarget,
} from '../helpers/smmOAuthReturn';

// Constants
import * as globalConstants from '../constants/screenConstants';
import {getLangCode, stringBundle, getPlan, getCountryCode} from '../stringBundle';
import useTransactionsUnified from '../customHooks/useTransactionsUnified';
import {useConnectionsDetails} from '../customHooks/useConnectionsDetails';
import {useParentLocationSync} from '../customHooks/useParentLocationSync';
import {
  buildScrollAwareViewportCenteredContent,
  isViewportCenteredModalContent,
  type CenteredModalSizeSpecStyle,
} from '../customHooks/useCenteredModalSizeSpec';
const ScamProtection = lazy(() => import('./scamProtection/ScamProtection' /* webpackChunkName: "scamprotection" */));
const ScamProtectionFinancialDetails = lazy(
  () => import('./scamProtection/ScamProtectionFinancialDetails' /* webpackChunkName: "scamprotectionfinancial" */)
);
const ScamProtectionRecovery = lazy(
  () => import('./scamProtection/ScamProtectionRecovery' /* webpackChunkName: "scamprotectionrecovery" */)
);
const ScamProtectionRecoveryPlan = lazy(
  () => import('./scamProtection/ScamProtectionRecoveryPlan' /* webpackChunkName: "scamprotectionrecoveryplan" */)
);
const ScamProtectionClaim = lazy(
  () => import('./scamProtection/ScamProtectionClaim' /* webpackChunkName: "scamprotectionclaim" */)
);
import {clientsUsingAvast3Design} from '../constants/partnerConstants';

// Page Components
const SummaryOfRights = lazy(() => import('../components/appObserver/AppSummaryOfRights'));
const AdobeBannerList = lazy(
  () => import('../containers/banner/AdobeBannerList' /* webpackChunkName: "abtestbanner" */)
);
const Dashboard = lazy(() => import('../components/dashboard/Dashboard' /* webpackChunkName: "dashboard" */));
const BureauRedirect = lazy(() => import('../components/credit/BureauRedirect' /* webpackChunkName: "bureau" */));
const ModalRoot = lazy(() => import('../containers/common/ModalRoot' /* webpackChunkName: "modal" */));
const AlertList = lazy(() => import('./alerts/AlertList' /* webpackChunkName: "alertlist" */));
const AlertListAvast3 = lazy(() => import('./alerts/avast3/AlertList' /* webpackChunkName: "alertlistavast3" */));
const Credit = lazy(() => import('../containers/credit/Credit' /* webpackChunkName: "credit" */));
const CreditAU = lazy(() => import('../containers/credit/CreditAU' /* webpackChunkName: "creditAU" */));
const CreditMonitor = lazy(
  () => import('./credit/creditMonitor/CreditMonitor' /* webpackChunkName: "creditMonitor" */)
);
const MonitoringInfo = lazy(() => import('./monitoring/MonitoringInfo' /* webpackChunkName: "monitoring" */));
const DarkWebLanding = lazy(() => import('./alerts/DarkWebLanding' /* webpackChunkName: "darkweblanding" */));
const AlertPreferences = lazy(
  () => import('../containers/monitoring/AlertPreferences' /* webpackChunkName: "alertpref" */)
);
const AlertsLanding = lazy(() => import('../components/alerts/AlertsLanding' /* webpackChunkName: "alertsland" */));
const CaseDetails = lazy(() => import('./restoration/CaseDetails' /* webpackChunkName: "casedetail" */));
const RestorationCases = lazy(() => import('./restoration/CaseList' /* webpackChunkName: "caselist" */));
const EstatePlanning = lazy(() => import('./estatePlanning/EstatePlanning' /* webpackChunkName: "estateplanning" */));
const EstatePlanningDocumentsPage = lazy(
  () => import('./estatePlanning/postPurchase/EstatePlanningDocumentsPage' /* webpackChunkName: "estateplanning" */)
);
const EstatePlanningNotarizePage = lazy(
  () => import('./estatePlanning/postPurchase/EstatePlanningNotarizePage' /* webpackChunkName: "estateplanning" */)
);
const EstatePlanningFundTrustPage = lazy(
  () => import('./estatePlanning/postPurchase/EstatePlanningFundTrustPage' /* webpackChunkName: "estateplanning" */)
);
const Transactions = lazy(() => import('../containers/Transactions' /* webpackChunkName: "transactions" */));
const TransactionsUnified = lazy(
  () => import('./transactionsUnified/TransactionsUnified' /* webpackChunkName: "transactionsUnified" */)
);
const TransactionsSearch = lazy(
  () => import('../containers/transactions/TransactionsSearch' /* webpackChunkName: "transactionssearch" */)
);
const ErrorPage = lazy(() => import('../components/ErrorPage' /* webpackChunkName: "errorpage" */));
const TuSummaryOfRights = lazy(() => import('../components/TuSummaryOfRights' /* webpackChunkName: "turights" */));
const ItpsLocksAndFreeze = lazy(() => import('./locks/ItpsLocksAndFreeze' /* webpackChunkName: "locks" */));
const SocialMediaMonitoring = lazy(
  () => import('../containers/monitoring/socialMediaMonitoring/SocialMediaMonitoring' /* webpackChunkName: "smm" */)
);
const PrivacyAdvisor = lazy(() => import('./privacyAdvisor/PrivacyAdvisor' /* webpackChunkName: "privacyadvisor" */));
const PrivacyMonitor = lazy(() => import('./PrivacyMonitor' /* webpackChunkName: "privacymonitor" */));
const HomeTitleMonitoring = lazy(() => import('./homeTitle/HomeTitleMonitoring' /* webpackChunkName: "hometitle" */));
const VerifyHomeTitle = lazy(() => import('./homeTitle/VerifyHomeTitle' /* webpackChunkName: "verifyHometitle" */));
const MemberChatShell = lazy(
  () => import('../containers/memberChat/MemberChatShell' /* webpackChunkName: "memberchat" */)
);
const PlanDetails = lazy(() => import('./planDetails/PlanDetails' /* webpackChunkName: "plandetails" */));
const RecurringTransactionDetailView = lazy(() => import('./transactions/RecurringTransactionDetailView'));
const AppObserver = lazy(() => import('../components/appObserver/AppObserver' /* webpackChunkName: "AppObserver" */));
const TransactionsSearchUnified = lazy(
  () => import('./transactionsUnified/TransactionsSearchUnified' /* webpackChunkName: "transactionssearchunified" */)
);
const CreditOffersRedirect = lazy(() => import('../components/redirects/CreditOffersRedirect'));
const Marketplace = lazy(
  () => import('../containers/creditOffers/marketplace/MarketplaceContainer' /* webpackChunkName: "marketplace" */)
);

const ScamVerification = lazy(() => import('./scamVerification/ScamVerification'));
const ScamVerificationCaseDetails = lazy(() => import('./scamVerification/ScamVerificationCaseDetails'));
const Genie = lazy(() => import('../components/genie/Genie' /* webpackChunkName: "genie" */));
const Discover = lazy(() => import('./discover/Discover' /* webpackChunkName: "discover" */));
const CardShieldLanding = lazy(
  () => import('./cardShield/CardShieldLanding' /* webpackChunkName: "cardshieldlanding" */)
);
const CardShieldPastRemovalsPage = lazy(
  () => import('./cardShield/CardShieldPastRemovalsPage' /* webpackChunkName: "cardshieldpastremovals" */)
);
const CardShieldTrustedMerchantsPage = lazy(
  () => import('./cardShield/CardShieldTrustedMerchantsPage' /* webpackChunkName: "cardshieldtrustedmerchants" */)
);
const CardShieldMerchantDetailPage = lazy(
  () => import('./cardShield/CardShieldMerchantDetailPage' /* webpackChunkName: "cardshieldmerchantdetail" */)
);
const CardShieldEntryResolver = lazy(
  () => import('./cardShield/CardShieldEntryResolver' /* webpackChunkName: "cardshieldentryresolver" */)
);

// Object containing partner token and client identifier.  Used for establishing a Member API session for partner's user.
type PartnerData = {
  nslToken?: string; // NSL token used to establish a session for partner's user.  Prefer to use partner token if available.
  partnerToken?: string; // partner token used to establish a session for partner's user
  clientId?: string; // client identifier, used to identify the client calling Member API
  cssUrls?: string[]; // array of URLs for CSS files to be loaded for the partner corresponding to clientId
  partnerConfig?: PartnerConfig; // object containing string values to override support URLs and phone numbers
  theme?: string; // value to set in the data-theme attribute of 'html' element
};

export const App = () => {
  const [state, setState] = useState({
    loggedOut: false,
    getMode: false,
    needLifeLockTermsConditions: false,
    needsDSPOnboarding: false,
    showQuebecReimbursementOptIn: false,
    showFcraSuccessModal: false,
    fetchUserData: false,
    showingModal: false,
  });
  const localizedStringBundle: StringMap = stringBundle(getLangCode(), null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigationType = useNavigationType();
  const auth = useSelector((store: RootState) => store.auth);
  const countryCode = utils.safeGet(auth, 'user.countryCode', '');
  const {
    data: privacyLicense,
    isLoading: isPrivacyLicenseLoading,
    isBlocked: isPrivacyLicenseBlocked,
  } = usePrivacyMonitorLicense(countryCode);
  const isPrivacyLicensePending = isPrivacyLicenseLoading || isPrivacyLicenseBlocked;

  // Get product features for unified components check
  const productFeatures = utils.safeGet(auth, 'user.primaryMember.plan.productFeatures', {});
  // gate the Reimagine (Norton ODS) theme; the legacy theme is retained for ineligible users.
  const reimagineThemeEligible = utils.isReimagineThemeEligible(auth);
  const isPlanLoaded = !_isEmpty(utils.safeGet(auth, 'user.primaryMember.plan', {}));
  const isYodleeMigrationEnabled = utils.isYodleeMigrationEnabled(productFeatures);
  const isTxmJpEnabled = utils.isTxmJpAvailable(productFeatures);
  const {mutate: getFastLinkToken} = useFastLinkToken();
  const {fetchTxmData} = useTransactions();
  const isValidLicense = isPrivacyMonitorLicenseValid(privacyLicense);
  const propModal = useSelector((store: RootState) => store.modal);
  const propModalRef = useRef(propModal);
  propModalRef.current = propModal;
  const {fetchInboxAlerts} = useAlerts();
  const {isError} = useQuebecOptIn();
  const errorStatus = utils.safeGet(auth, 'loginError.result.status', null);
  const showFcraColp = utils.safeGet(auth, 'user.primaryMember.plan.showFcraColp', false);
  const {fcraState} = useFcraConsent();
  const {isAccepted = false, isSuccessModalShown = false} = fcraState;
  const prevNeedLifeLockTermsConditions = usePrevious(state.needLifeLockTermsConditions);
  const prevShowQuebecReimbursementOptIn = usePrevious(state.showQuebecReimbursementOptIn);
  const prevIsQuebecOptInError = usePrevious(isError);
  const prevShowFcraSuccessModal = usePrevious(state.showFcraSuccessModal);
  const {initCreditData} = useCreditData();
  const fetchTxmDataForJp = isTxmJpEnabled ? fetchTxmData : async() => undefined;
  const analytics = useAnalytics();
  const userActivity = useUserActivity();
  const useDspUi = shouldUseDspUi(auth);
  const useDwmUi = shouldUseDwmUi(auth);
  const accountId = utils.safeGet(auth, 'user.primaryMember.accountId', '');
  const hasRefreshFailedRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const hasFetchedDataRef = useRef(false);
  const isValidLicenseRef = useRef(isValidLicense);
  isValidLicenseRef.current = isValidLicense;
  const countryCodeRef = useRef(countryCode);
  countryCodeRef.current = countryCode;
  const useDspUiRef = useRef(useDspUi);
  useDspUiRef.current = useDspUi;
  const {data: connectionsResponse} = useConnectionsDetails(accountId, productFeatures);
  const connectionsData = utils.safeGet(connectionsResponse, 'connections', []);
  const hasInstitutions = !_isEmpty(connectionsData);
  const {isMLCardOffersActive, isCreditOffersMarketplaceActive, isSavingsOffersMarketplaceActive} = useFinancialOffers(
    productFeatures,
    localizedStringBundle
  );
  useTransactionsUnified(accountId, productFeatures, hasInstitutions);

  let ngpAccessToken = '';
  let xNlokTraceId = '';
  let ngpTenantName = 'norton';
  const isAgent = utils.safeGet(auth, 'user.isAgent', false);
  const appContainerRef: React.RefObject<HTMLDivElement> = useRef(null);
  const expiryTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const lastWindowSpecRef = useRef<GetWindowSpecPayload | null>(null);

  // State for mobile mode fixed menu
  const [isMobileMode, setIsMobileMode] = useState(
    window.innerWidth < globalConstants.MEDIUM_SCREEN_MAX_WIDTH || window.REACT_APP_COLLAPSE_LEFT_NAV
  );
  const isMobileModeRef = useRef(isMobileMode);
  isMobileModeRef.current = isMobileMode;

  const appContainerClasses = classNames({
    'flex-row justify-between min-h-104 sm:min-h-0 bg-background': useDspUi,
    'lg:flex': !window.REACT_APP_COLLAPSE_LEFT_NAV && useDspUi,
  });
  const isPublicRoute =
    [
      '/bureauredirect',
      '/tuSummaryOfRights',
      '/appObserver',
      '/advanceDispositionFragment',
      '/disposition/alertConfirmedValidFragment',
      '/disposition/alertConfirmedInvalidFragment',
      '/disposition/custodianAlertConfirmedValidFragment',
      '/disposition/custodianAlertConfirmedInvalidFragment',
      '/disposition/alertDispositionedFragment',
      '/disposition/error',
      '/pageNotAvailable',
      '/externalredirect/creditoffers',
      '/summaryOfRights',
    ].indexOf(utils.safeGet(location, 'pathname')) !== -1;
  const isInterstitialPage = utils.safeGet(location, 'pathname', '').startsWith('/externalredirect');
  const appClasses = classNames(
    'flex flex-col w-full',
    isInterstitialPage ? 'h-screen' : 'h-full',
    useDwmUi ? 'max-w-3xl m-auto pb-12 overflow-auto' : 'overflow-auto'
  );
  const appBackgroundClasses = classNames(
    'flex-1 m-auto min-h-96 app',
    utils.safeGet(location, 'pathname', '') === '/error' ? '' : 'w-full bg-background',
    utils.safeGet(location, 'pathname', '').includes('/privacymonitor') ? 'h-full 4xl:max-w-4xl' : '3xl:max-w-3xl'
  );

  const whiteLabelClientDetail = utils.safeGet(auth, 'whiteLabelClient', '');
  const marketingDetails = utils.safeGet(auth, 'user.primaryMember.plan.marketingDetails', {});
  const associatedMembers = utils.safeGet(auth, 'user.associatedMembers', []);

  // Conditional AlertList component based on client design
  const AlertListComponent = clientsUsingAvast3Design.includes(whiteLabelClientDetail) ? AlertListAvast3 : AlertList;

  const hasAccess = useCallback(
    (path: string, whitelabel = true, marketDetail = true): boolean => {
      if (
        path === '/social-media-monitoring/add-account-success' ||
        path === '/social-media-monitoring/add-account-error'
      ) {
        return utils.hasAccessToRoute(
          path,
          productFeatures,
          marketDetail ? marketingDetails : {},
          whitelabel ? whiteLabelClientDetail : '',
          associatedMembers
        );
      } else {
        return utils.hasAccessToRoute(
          path,
          productFeatures,
          marketDetail ? marketingDetails : {},
          whitelabel ? whiteLabelClientDetail : '',
          {
            isMLCardOffersActive,
            isOffersMarketplaceActive: isCreditOffersMarketplaceActive || isSavingsOffersMarketplaceActive,
            membershipCountryCode: countryCode || 'US',
            privacyLicense,
            isPrivacyLicensePending,
            auth,
          }
        );
      }
    },
    [
      productFeatures,
      marketingDetails,
      privacyLicense,
      isPrivacyLicensePending,
      whiteLabelClientDetail,
      associatedMembers,
      isMLCardOffersActive,
      isCreditOffersMarketplaceActive,
      isSavingsOffersMarketplaceActive,
      countryCode,
      auth,
    ]
  );

  /**
   * Sends an app loaded message to the parent window.
   */
  const sendAppLoadedMessage = () => {
    utils.sendMessage(
      {
        type: 'APP_LOADED',
        payload: {
          message: 'app was loaded',
          tokenExchangeEnabled: window.REACT_APP_ENABLE_OIDC_TOKEN_EXCHANGE === true,
        },
      },
      window.REACT_APP_PARENT_HOST
    );
  };

  /**
   * Handles the redirect issue for FB / Instagram account linking.
   * NGP may send encoded OAuth stub pathnames (e.g. /_%3D_, /*%3D*); updateLocationHash decodes them
   * (e.g. /_=_, /*=*). Match encoded and decoded stubs so redirectUrl navigation still runs.
   */
  const handleRedirectIssue = useCallback(() => {
    const pathname = location?.pathname || '';
    const isOAuthStub = isSocialMediaOAuthRedirectStubPathname(pathname);

    if (isOAuthStub) {
      const search = location?.search || '';
      const target = resolveSmmOAuthStubNavigateTarget(search);

      if (target) {
        const targetQueryIndex = target.indexOf('?');
        const targetSearch = targetQueryIndex >= 0 ? target.slice(targetQueryIndex) : search;
        const urlQueryString = utils.parseQueryString(targetSearch);
        if (!_isEmpty(urlQueryString)) {
          dispatch(setModalQuery(urlQueryString));
        }
        navigate(target, {replace: true});
      } else {
        const cancelTarget = resolveSmmOAuthCancelReturnTarget();
        if (cancelTarget) {
          clearSmmPendingOAuthReturn();
          navigate(cancelTarget, {replace: true});
        } else {
          navigate('/', {replace: true}); // Default fallback
        }
      }
    }
  }, [navigate, location, dispatch]);

  useEffect(() => {
    handleRedirectIssue();
  }, [handleRedirectIssue]);

  /**
   * Gets the specifications of the parent window.
   */
  const getParentWindowSpec = () => {
    utils.sendMessage({type: 'GET_PARENT_WINDOW_SPEC', payload: {}}, window.REACT_APP_PARENT_HOST);
  };

  /**
   * Handles window resize to update mobile mode state
   */
  const handleResize = () => {
    setIsMobileMode(window.innerWidth < globalConstants.MEDIUM_SCREEN_MAX_WIDTH || window.REACT_APP_COLLAPSE_LEFT_NAV);
  };

  /**
   * Sets up event listeners for the app.
   */
  const setupEventListeners = () => {
    window.addEventListener('beforeunload', handleAppUnload);
    window.addEventListener('message', receiveMessage, false);
    window.addEventListener('resize', handleResize);
  };

  /**
   * Cleans up event listeners for the app.
   */
  const cleanupEventListeners = () => {
    window.removeEventListener('message', receiveMessage, false);
    window.removeEventListener('beforeunload', handleAppUnload);
    window.removeEventListener('resize', handleResize);
  };

  useEffect(() => {
    utils.polyfillsForIe(); // IE 11 polyfill methods
    sendAppLoadedMessage();
    getParentWindowSpec();
    setupEventListeners();
    utils.resizeIframe();

    return () => {
      clearTimeout(expiryTimerRef.current);
      cleanupEventListeners();
    };
  }, []);

  // apply the Reimagine (Norton ODS) theme once the member response is known
  // (primaryMember.showReimaginedExperience); otherwise the legacy theme is retained. Gated on plan-loaded
  // so we don't toggle the theme before the member call returns. For eligible members, light vs dark
  // follows the OS color-scheme preference and updates live when the member changes that setting.
  useEffect(() => {
    if (!isPlanLoaded) {
      return undefined;
    }
    syncReimagineTheme(reimagineThemeEligible);
    if (!reimagineThemeEligible || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleColorSchemeChange = () => syncReimagineTheme(reimagineThemeEligible);
    if (colorSchemeQuery.addEventListener) {
      colorSchemeQuery.addEventListener('change', handleColorSchemeChange);
    } else if (colorSchemeQuery.addListener) {
      // Safari < 14 fallback
      colorSchemeQuery.addListener(handleColorSchemeChange);
    }
    return () => {
      if (colorSchemeQuery.removeEventListener) {
        colorSchemeQuery.removeEventListener('change', handleColorSchemeChange);
      } else if (colorSchemeQuery.removeListener) {
        colorSchemeQuery.removeListener(handleColorSchemeChange);
      }
    };
  }, [isPlanLoaded, reimagineThemeEligible]);

  // The first GET_WINDOW_SPEC from the parent typically arrives before PARTNER_LOGIN / INIT
  // completes, so useDspUi is still false and setAppContainerHeight falls through to the
  // Math.max(screen.availHeight, windowHeight) branch. Once auth loads and useDspUi flips
  // to true, we re-apply the cached window spec so min-height is recalculated using the
  // correct windowHeight-only path for mobile iframe contexts.
  useEffect(() => {
    if (useDspUi && isMobileMode && lastWindowSpecRef.current) {
      setAppContainerHeight(lastWindowSpecRef.current);
    }
  }, [useDspUi, isMobileMode]);

  /**
   * Redirects to NGP dashboard if the user is not DSP, not DWM, and not viewing this SPA from the mobile app.
   */
  useEffect(() => {
    if (!_isEmpty(auth.user) && !useDspUi && !useDwmUi && !auth.isMobileApp) {
      const whiteLabelClient = utils.safeGet(auth, 'whiteLabelClient', '');
      // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
      window.top.location.replace(utils.getNgpDashboardLink(whiteLabelClient));
    }
  }, [auth, useDspUi, useDwmUi]);

  /**
   * Handles situations where Member API returns an error code.
   * Either redirects to an external URL, or uses react router to change the path to this SPA's error page.
   * @param errorCode
   * @param trackId
   */
  const handleErrorPage = useCallback(
    (errorCode: number, trackId: string, partnerClientId = '') => {
      if (errorCode === errorCodes.AUTH_NO_ACCOUNT_FOUND_APPLE_RELAY_EMAIL) {
        utils.sendMessage(
          {type: 'CHANGE_WINDOW_LOCATION', payload: utils.getIdentityEnrollmentLink(partnerClientId)},
          window.REACT_APP_PARENT_HOST
        );
      } else {
        const searchStr = 'errorCode=' + errorCode + '&trackId=' + trackId + '&partnerClientId=' + partnerClientId;
        setState((prevState) => ({
          ...prevState,
          getMode: true,
        }));
        navigate(`/error?${searchStr}`);
      }
    },
    [navigate]
  );

  /**
   * Handles rate limiting error status.
   */
  useEffect(() => {
    if (errorStatus === 429) {
      handleErrorPage(errorCodes.API_RATE_LIMIT_HIT, ''); // no trackId is available when limit is hit
    }
  }, [errorStatus, handleErrorPage]);

  /**
   * Handles various modals and redirects
   */
  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      showingModal: true,
    }));

    if (
      // Handle LifeLock terms and conditions
      window.REACT_APP_ENABLE_LIFELOCK_WELCOME === true &&
      prevNeedLifeLockTermsConditions !== state.needLifeLockTermsConditions &&
      state.needLifeLockTermsConditions === true
    ) {
      utils.sendMessage(
        {type: 'CHANGE_WINDOW_LOCATION', payload: utils.getLsaLink(whiteLabelClientDetail)},
        window.REACT_APP_PARENT_HOST
      );
    } else if (
      state.showQuebecReimbursementOptIn &&
      state.showQuebecReimbursementOptIn !== prevShowQuebecReimbursementOptIn
    ) {
      // Handle Quebec reimbursement opt-in
      setTimeout(() => {
        dispatch({
          type: 'SHOW_MODAL',
          modalType: 'QUEBEC_INSURANCE_OPT_IN_MODAL',
          modalProps: {},
        });
      }, 0);
    } else if (isError && isError !== prevIsQuebecOptInError) {
      // Handle Quebec opt-in error
      setTimeout(() => {
        dispatch({
          type: 'SHOW_MODAL',
          modalType: 'CONSENT_ERROR_MODAL',
          modalProps: {},
        });
      }, 0);
    }
  }, [
    state.needLifeLockTermsConditions,
    prevNeedLifeLockTermsConditions,
    state.showQuebecReimbursementOptIn,
    prevShowQuebecReimbursementOptIn,
    isError,
    prevIsQuebecOptInError,
    whiteLabelClientDetail,
    dispatch,
  ]);

  /**
   * Updates the user session 1 minute before the lifelock token expiry (20 min) for active user.
   * NGP will logout the user who is inactive for 15 min.
   */
  // Add a ref to track refresh token failure
  useEffect(() => {
    const whiteLabelClient = utils.safeGet(auth, 'whiteLabelClient', '');
    const primaryMember = utils.safeGet(auth, 'user.primaryMember', null);

    if (window.REACT_APP_ENABLE_REFRESH_TOKEN === true && !_isEmpty(primaryMember) && !whiteLabelClient) {
      const expiry = utils.safeGet(auth, 'user.expiry', null);
      const timeout = expiry && expiry * 1000 - 60 * 1000 - Date.now();
      const productFeatures = utils.safeGet(auth, 'user.primaryMember.plan.productFeatures', {});

      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
      }

      if (timeout && timeout > 0) {
        expiryTimerRef.current = setTimeout(() => {
          // Check if refreshing is in progress or a previous refresh attempt has failed
          if (isRefreshingRef.current || hasRefreshFailedRef.current) {
            return;
          }

          isRefreshingRef.current = true;
          dispatch(refreshToken()).then((res: $TSFixMe) => {
            if (res.type === 'REFRESH_TOKEN_SUCCESS') {
              hasRefreshFailedRef.current = false; // Reset failure flag on success

              // Refresh Privacy Web SDK session if the user is entitled to Privacy Monitor.
              // window.privacyWebSdkRefreshSession is registered by PrivacyMonitor.tsx on mount
              // and includes its own error handling. See src/containers/PrivacyMonitor.tsx.
              if (window.REACT_APP_LABEL !== 'production') {
                utils.log({
                  message: `LL Refresh success; initiate PMA refresh? ${
                    isValidLicense && typeof window.PrivacyWebSDK?.refreshSession === 'function'
                  } | isValidLicense=${isValidLicense} | PrivacyWebSDK exists=${!!window.PrivacyWebSDK} | refreshSession type=${typeof window
                    .PrivacyWebSDK?.refreshSession}`,
                });
              }
              if (isValidLicense && typeof window.privacyWebSdkRefreshSession === 'function') {
                window.privacyWebSdkRefreshSession();
              }

              if (
                utils.txmVisible(productFeatures) &&
                !utils.isTxmJpAvailable(productFeatures) &&
                !utils.isFeaturePresent(productFeatures, 'financialWellness') &&
                !utils.isFeaturePresent(productFeatures, 'fwMultiAggregator') &&
                isYodleeMigrationEnabled
              ) {
                getFastLinkToken({useCache: false});
              }
            } else {
              hasRefreshFailedRef.current = true; // Set failure flag on failure
            }
            isRefreshingRef.current = false;
          });
        }, timeout);
      }
    }
  }, [auth, dispatch, isValidLicense]);

  /**
   * Handles the FCRA success modal.
   */
  useEffect(() => {
    const showFcraColp = utils.safeGet(auth, 'user.primaryMember.plan.showFcraColp', false);

    setState((prevState) => {
      const updatedState = {
        ...prevState,
        showingModal: true,
        showFcraSuccessModal: showFcraColp && isAccepted && !isSuccessModalShown,
      };

      if (prevShowFcraSuccessModal !== updatedState.showFcraSuccessModal && updatedState.showFcraSuccessModal) {
        setTimeout(() => {
          dispatch({
            type: 'SHOW_MODAL',
            modalType: 'FCRA_SUCCESS_MODAL',
            modalProps: {},
          });
        }, 0);
      }

      return updatedState;
    });
  }, [isAccepted, isSuccessModalShown, auth, prevShowFcraSuccessModal, dispatch, setState]);

  /**
   * Handles fetching user's alert, credit and txm data.
   */
  useEffect(() => {
    // GUTS-3965 Check accountGuid/nortonGuid (not sure which is reliably present), since accountGuid is gone as of April 2024
    const useDwmUi = shouldUseDwmUi(auth);
    const primaryMemberNortonGuid = utils.safeGet(auth, 'user.primaryMember.nortonGuid');
    const primaryMemberAccountId = utils.safeGet(auth, 'user.primaryMember.accountId');
    // Fetch user data if the user is authenticated and the data has not been fetched yet, do not fetch this data if user is using DWM UI as they only need dwm alert fetch
    if (
      (!_isEmpty(primaryMemberNortonGuid) || !_isEmpty(primaryMemberAccountId)) &&
      state.fetchUserData === true &&
      !useDwmUi &&
      !hasFetchedDataRef.current // Guard to prevent duplicate execution
    ) {
      hasFetchedDataRef.current = true; // Mark as executed
      analytics.setupAnalytics();
      fetchInboxAlerts();
      fetchTxmDataForJp();
      initCreditData();
      setState((prevState) => ({
        ...prevState,
        fetchUserData: false,
      }));
    }
  }, [auth, state.fetchUserData, fetchInboxAlerts, fetchTxmDataForJp, initCreditData, analytics]);

  /**
   * Updates the height of the iframe on every render if needed.
   */
  useEffect(() => {
    utils.resizeIframe();
  });

  /**
   * Sends a message to the parent host when the app is unloaded.
   */
  const handleAppUnload = () => {
    utils.sendMessage({type: 'APP_UNLOADED', payload: {}}, window.REACT_APP_PARENT_HOST);
  };

  const startAuthentication = (ngpData: NgpData) => {
    if (window.REACT_APP_ENABLE_OIDC_TOKEN_EXCHANGE === true && ngpData.ngpAccessToken) {
      startTokenExchange(ngpData.ngpAccessToken).then((data) => {
        const errorCode = data.payload && data.payload.errorCode;
        const subscriptionErrorcode = data.payload && data.payload.subscriptionErrorcode;
        const trackId = data.payload && data.payload.trackId;
        updateNslToken(utils.safeGet(ngpData, 'ngpAccessToken', ''));

        if (data.type === 'LOGGED_IN') {
          // set nsl access token and load spa data on user login

          loadSpaData(data.payload);
        } else if (
          data.type === 'LOGIN_ERROR' &&
          (errorCode === errorCodes.OIDC_EXCHANGE_TOKEN_ERROR ||
            errorCode === errorCodes.INVALID_NGP_ACCESS_TOKEN ||
            errorCode === errorCodes.MISSING_MAY_ACT_CLAIM_ERROR)
        ) {
          utils.sendMessage({type: 'LOGIN_ERROR', payload: ngpData.ngpAccessToken}, window.REACT_APP_PARENT_HOST);
        } else if (data.type === 'LOGIN_ERROR' && errorCodes.AUTH_LSA_REQUIRED_NOT_ACCEPTED === subscriptionErrorcode) {
          utils.sendMessage(
            {type: 'CHANGE_WINDOW_LOCATION', payload: utils.getLsaLinkOn20008Error()},
            window.REACT_APP_PARENT_HOST
          );
        } else {
          handleErrorPage(errorCode, trackId);
        }
      });
    } else {
      return startNslHandshake();
    }
  };

  /**
   * Checks messages posted to this window and takes action as necessary.
   * type window.postMessage in console to simulate a message from NGP, such as "INIT"
   * @param event The message event.
   */
  const receiveMessage = (event: MessageEvent) => {
    if (
      window.REACT_APP_PARENT_HOST !== '*' &&
      event.origin.indexOf(window.REACT_APP_PARENT_HOST) === -1 &&
      event.origin !== window.location.origin &&
      event.origin + ':3000' !== window.location.origin
    ) {
      // TODO: remove the :3000 after nsl redirect to local includes :3000 in url
      return;
    }
    let ngpRedirect = '';
    // data object sent via window.postMessage from NGP page to the SPA iframe

    let ngpData: NgpData = {}; // stores data from NGP, including anonymous tracking values the SPA includes in Member API calls
    let partnerData: PartnerData = {};
    const paspaIframe = document.getElementById('paspa') as HTMLIFrameElement;

    switch (event.data.type) {
      case 'LOAD_PARTNER_WIDGET': {
        // process partner-provided widget config for dashboard
        if (event.data.payload && typeof event.data.payload === 'object') {
          const cfg = utils.safeGet(event, 'data.payload.partnerWidget', null);
          if (cfg && typeof cfg === 'object') {
            dispatch({type: 'SET_PARTNER_WIDGET', partnerWidget: cfg});
          }
        }
        break;
      }
      case 'APP_GET_METADATA':
        if (paspaIframe && paspaIframe.contentWindow) {
          paspaIframe.contentWindow.postMessage(
            {
              type: 'APP_METADATA',
              payload: {
                culture: getLangCode(),
                initFrom: 'll-web',
                countryCode: getCountryCode(),
                accessToken: ngpAccessToken,
                xNlokTraceId,
                flow: 'privacyadvisor',
                tenant: ngpTenantName,
                plan: getPlan(),
              },
            },
            window.REACT_APP_PRIVACY_ADVISOR_HOST
          );
        }
        break;
      case 'APP_ERROR':
        navigate('/error?errorCode=' + event.data.payload.errorCode, {replace: true});
        break;
      case 'PRIVACY_ADVISOR_RESIZED':
        // resize the Privacy Advisor SPA iframe based on height received from Privacy Advisor SPA
        paspaIframe.style.minHeight = `${event.data.payload}px`;
        utils.resizeIframe();
        break;
      case 'PRIVACY_ADVISOR_SCROLL_PARENT':
        // Scroll the parent window to focus on the Privacy Advisor SPA iframe top based
        // on event received from Privacy Advisor SPA
        utils.scrollToSection('paspa', ['paspa']);
        break;
      case 'LOGGED_OUT':
        ngpRedirect = window.location.href.replace(window.location.protocol + '//' + window.location.hostname, '');
        ngpRedirect = ngpRedirect.replace(':' + window.location.port, '');
        utils.sendMessage({type: 'LOGGED_OUT', payload: ngpRedirect}, window.REACT_APP_PARENT_HOST);
        setState((prevState) => ({
          ...prevState,
          loggedOut: true,
        }));
        break;
      case 'PARTNER_LOGIN':
        // Starting point for partner pages using NSL token to log their user into the SPA.
        // Initialize anything else necessary for partner user.
        if (event.data.payload && typeof event.data.payload === 'object') {
          try {
            partnerData = event.data.payload;
          } catch (error) {
            partnerData = {};
          }
        }
        if (!partnerData.nslToken || !partnerData.clientId) {
          utils.sendMessage(
            {type: 'PARTNER_LOGIN_ERROR', payload: {message: 'missing nslToken or clientId'}},
            window.REACT_APP_PARENT_HOST
          );
          break;
        }
        // Partner parent pages own NSL token lifetime (`PARTNER_LOGIN` / `PARTNER_EXTEND_ACCESS`); use `setNslToken` only.
        setNslToken(utils.safeGet(partnerData, 'nslToken', ''));
        stopNslTokenRefresh();
        authWithNslToken(partnerData.nslToken, partnerData.clientId).then((result) => {
          const payload = utils.safeGet(result, 'payload', {});
          if (payload && payload.accessToken) {
            // notify parent container of success
            utils.sendMessage(result, window.REACT_APP_PARENT_HOST);
            // loadSpaData will try to load the predefined theme that corresponds to the whiteLabelClient value.
            // The parent container of this SPA also has the option of listening for the result from utils.sendMessage, and then
            // sending a LOAD_CSS_URLS message to the SPA to theme it, followed by a OVERRIDE_CONFIGS message to the SPA
            // to customize support URLs and phone numbers.
            loadSpaData({
              whiteLabelClient: partnerData.clientId,
              accessToken: utils.safeGet(result, 'payload.accessToken', ''), // accessToken is only returned by Member API for clients that do not use session cookie
            });
          } else if (payload && payload.errorCode) {
            const errorCode = utils.safeGet(payload, 'errorCode', '');
            const trackId = utils.safeGet(payload, 'trackId', '');
            handleErrorPage(errorCode, trackId, partnerData.clientId);
          }
        });
        break;
      case 'PARTNER_EXTEND_ACCESS':
        // Starting point for partner pages using NSL token to extend the user's SPA access time.
        // We avoid calling this "session" time because there is no session cookie and no corresponding Member API session.
        // The underlying process involves refreshing the Member API encrypted access token.
        if (event.data.payload && typeof event.data.payload === 'object') {
          partnerData = event.data.payload;
        }
        if (!partnerData.nslToken || !partnerData.clientId) {
          utils.sendMessage(
            {type: 'PARTNER_EXTEND_ACCESS_ERROR', payload: {message: 'missing nslToken or clientId'}},
            window.REACT_APP_PARENT_HOST
          );
          break;
        }
        // Partner parent pages own NSL token lifetime (`PARTNER_LOGIN` / `PARTNER_EXTEND_ACCESS`); use `setNslToken` only.
        setNslToken(utils.safeGet(partnerData, 'nslToken', ''));
        stopNslTokenRefresh();
        authWithNslToken(partnerData.nslToken, partnerData.clientId).then((result) => {
          // notify parent container of success
          if (typeof result === 'object') {
            // modify type to indicate the message was a result of the PARTNER_EXTEND_ACCESS message
            switch (result.type) {
              case 'PARTNER_LOGIN_SUCCESS':
                result.type = 'PARTNER_EXTEND_ACCESS_SUCCESS';
                // accessToken is only returned by Member API for clients that do not use session cookie
                setHeaderAccessToken(utils.safeGet(result, 'payload.accessToken', '')); // used by fetchRest.ts and api.ts to call Member API auth-required endpoints
                // Refresh Privacy Web SDK session if the user is entitled to Privacy Monitor.
                // window.privacyWebSdkRefreshSession is registered by PrivacyMonitor.tsx on mount
                // and includes its own error handling. See src/containers/PrivacyMonitor.tsx.
                if (window.REACT_APP_LABEL !== 'production') {
                  utils.log({
                    message: `White Label Client Token Refresh success; initiate PMA refresh? ${
                      isValidLicenseRef.current && typeof window.PrivacyWebSDK?.refreshSession === 'function'
                    } | isValidLicense=${
                      isValidLicenseRef.current
                    } | PrivacyWebSDK exists=${!!window.PrivacyWebSDK} | refreshSession type=${typeof window
                      .PrivacyWebSDK?.refreshSession}`,
                  });
                }
                if (isValidLicenseRef.current && typeof window.privacyWebSdkRefreshSession === 'function') {
                  try {
                    window.privacyWebSdkRefreshSession();
                  } catch (err) {
                    utils.log({
                      error: err,
                      message: `Failed to refresh Privacy Web SDK session for partner ${partnerData.clientId}`,
                    });
                  }
                }
                void invalidatePrivacyMonitorQueries(queryClient, countryCodeRef.current);
                break;
              case 'PARTNER_LOGIN_ERROR':
                result.type = 'PARTNER_EXTEND_ACCESS_ERROR';
                break;
              default:
                // prefix any unexpected types to indicate PARTNER_EXTEND_ACCESS origination
                result.type = 'PARTNER_EXTEND_ACCESS_' + result.type;
                break;
            }
            utils.sendMessage(result, window.REACT_APP_PARENT_HOST);
          }
        });
        break;
      case 'LOAD_CSS_URLS':
        // process message sent by partner page to load CSS files, and set data-theme attribute to partnerData.theme value
        if (event.data.payload && typeof event.data.payload === 'object') {
          partnerData = event.data.payload;
          if (partnerData.cssUrls) {
            loadCssUrls(partnerData.cssUrls);
            window.htmlEl = document.querySelector('html');
            if (window.htmlEl) {
              window.htmlEl.setAttribute('data-theme', partnerData.theme);
            }
          }
        }
        break;
      case 'OVERRIDE_CONFIGS':
        // process message sent by partner page to override support URLs and phone numbers specified in config.js
        if (event.data.payload && typeof event.data.payload === 'object') {
          partnerData = event.data.payload;
          if (partnerData.partnerConfig) {
            overrideConfigs(partnerData.partnerConfig);
          }
        }
        break;
      case 'INIT':
        // Parse data from NGP, which includes ngpUserHash, ngpSessionHash, NLokTraceId and ngp access token.
        // See sendInit method in iframeListeners.js
        if (event.data.payload) {
          try {
            ngpData = JSON.parse(event.data.payload);
            ngpAccessToken = ngpData.ngpAccessToken || '';
            xNlokTraceId = ngpData.NLokTraceId || '';
            ngpTenantName = ngpData.tenantName || 'norton';
            updateNslToken(ngpAccessToken);
            initTrackingValues(
              ngpData.ngpUserHash || '',
              ngpData.ngpSessionHash || '',
              ngpData.NLokTraceId || '',
              ngpData.tenantId || '',
              ngpData.tenantName || '',
              ngpData.pCode || ''
            );
          } catch (error) {
            ngpData = {};
          }
        }

        if (ngpData.recreateSession === true) {
          setState((prevState) => ({
            ...prevState,
            getMode: false,
          }));

          startAuthentication(ngpData);
        } else {
          // Note, comment out checkUser if you are developing locally and always want to go through OIDC flow
          // checkUser should pass on refresh for new flow...local has issue related to cors
          checkUser()
            .then((result) => {
              // NGP hashes match what is stored in user's session. Proceed to loading member data using existing session.
              // Get whiteLabelClient, if any, and send to loadSpaData to activate theme.
              // Clients using this flow utilize NSL, NGP and session cookies, and do not use an access token to call Member API auth-required endpoints
              let whiteLabelClient = sessionStorage.getItem('whiteLabelClient');

              // If sessionStorage is empty (new tab, browser restart, etc.) use value returned from check user
              const memxSessionWhiteLabelClient = _get(result, 'whiteLabelClient');
              if (memxSessionWhiteLabelClient) {
                sessionStorage.setItem('whiteLabelClient', memxSessionWhiteLabelClient);
                whiteLabelClient = memxSessionWhiteLabelClient;
              }
              return loadSpaData({whiteLabelClient});
            })
            .then((result) => {
              // If loadSpaData failed with a login failure, throw the result so the catch block initiates NSL handshake
              // This could occur if the Member API session's tokens for data-api are invalid
              if (result.type === 'LOGIN_FAILURE') {
                throw result;
              }
            })
            .catch((error) => {
              // Exception could be due to
              // 1) User first time logging in
              // 2) NGP hashes DO NOT match what is stored in user's session. Create a new session via OIDC flow.
              // 3) loadSpaData failed with a login failure
              // 4) rate limited
              const errorCode = error && error.errorCode;

              if (errorCode === errorCodes.API_RATE_LIMIT_HIT) {
                // @ts-expect-error TS(2554) FIXME: Expected 2 arguments, but got 1.
                handleErrorPage(errorCode); // no trackId is available when limit is hit
              } else {
                startAuthentication(ngpData);
              }
            });
        }
        /*
        Alternative is to always go through OIDC flow to ensure Norton member
        establishes their own member-api session.  Checking NGP hashes or always
        establishing a session via OIDC, are two routes that handle the edge case where Norton
        user 1 logs into DSP SPA, logs out using Norton logout link, Norton logout flow
        fails to trigger logout on member-api so user 1's member-api session is still active,
        Norton user 2 logs into DSP SPA on the same computer and reuses user 1's session.
        */
        //startNslHandshake(ngpData); // try OIDC flow to establish a new member-api session
        break;
      case 'LOCATION_HASH_CHANGE':
        updateLocationHash(event.data.payload.locationHash, event.data.payload.queryString);
        break;
      case 'GET_WINDOW_SPEC':
        lastWindowSpecRef.current = event.data.payload;
        setAppContainerHeight(event.data.payload);
        updateModalSize(event.data.payload);
        repositionBody(event.data.payload);
        setAppSizeScrollData(event.data.payload);
        utils.resizeIframe(event.data.payload);
        break;
      case 'LOGIN_ERROR': {
        // due to child iframe encountering an error while obtaining a single-use access code from identity provider
        const errorCode = event.data.payload && event.data.payload.errorCode;
        const trackId = event.data.payload && event.data.payload.trackId;
        handleErrorPage(errorCode, trackId);
        break;
      }
      case 'NSL_ACCESS_TOKEN_REFRESHED': {
        let refreshPayload: {ngpAccessToken?: string} = {};
        try {
          refreshPayload =
            typeof event.data.payload === 'string' ? JSON.parse(event.data.payload) : event.data.payload ?? {};
        } catch {
          refreshPayload = {};
        }
        const refreshedToken = refreshPayload.ngpAccessToken ?? '';
        if (refreshedToken) {
          resolvePendingNslTokenRefresh(refreshedToken);
        } else {
          rejectPendingNslTokenRefresh(new Error('Parent returned empty NSL access token'));
        }
        break;
      }
      case 'LOGGED_IN':
        // Process message sent by SPA's iframe that loaded accessCode.html, as part of the NSL OIDC flow when no NGP token is usable.
        // typically due to calling startNslHandshake leading to child iframe sending the single-use access code from identity provider
        // This flow does not apply to NortonLifeLock partner users, so there is no whiteLabelClient in the payload.
        loadSpaData(event.data.payload);
        break;
      case 'SSO_INIT':
        // directs this SPA to initialize a session with member-api using the provided SSO token
        // type SSO_INIT comes from post message from mobile apps
        dispatch(isMobileApp(true));
        if (event?.data?.payload?.nsl_token) {
          updateNslToken(event.data.payload.nsl_token);
        }
        startSso(event.data.payload)
          .then(() => {
            if (event.data.isMobileApp) {
              dispatch(alertDetailMobileMemberChat(event.data.alertDetail));
            }
            // check window.MODE and init user data as necessary. (similar to case LOGGED_IN)
            loadSpaData(null);
            // GUTS-1757 TODO verify with mobile team that white labeling is not required in this flow
          })
          .catch((error) => {
            log.error('receiveMessage SSO_INIT, error=', JSON.stringify(error));
          });
        break;
      case 'WINDOW_SCROLLED': {
        debouncedScrollHandler(event);
        break;
      }
      default:
        // dont do anything for unrecognized message
        break;
    }
  };

  /**
   * Debounce the scroll handler to minimize the number of times handleUserActivity and setScrollData are called.
   * Wrapped inside useCallback to ensure that the debounced function is not recreated on every render.
   */
  const debouncedScrollHandler = useCallback(
    _debounce((event) => {
      userActivity.handleUserActivity();
      setScrollData(event.data.payload);
    }, 25),
    [event, userActivity]
  );

  /**
   * Object containing post-auth response from Member API to this SPA
   * @typedef {Object} PostAuthApiResponse
   * @property {boolean} needLifeLockTermsConditions - whether to show t&c screen or not
   * @property {string} whiteLabelClient - Identifies the white label client associated with the current user. Used to determine which theme to activate.
   * @property {string} accessToken - encrypted LL JWT, used by clients to access Member API auth-required endpoints
   */

  /**
   * Starting point for loading member data for this SPA.  Should be called after receiveMessage detects
   * a LOGGED_IN event.
   * If the payload indicates the user is a NortonLifeLock partner's customer, the corresponding partner theme should be loaded by the SPA
   * and window.clientId should be set to the whiteLabelClient value for use with subsequent calls to Member API
   * @param {PostAuthApiResponse} payload - Contains extra information such as whether to show the welcome screen, and the business partner identifier
   */
  // @ts-expect-error TS(2304) FIXME: Cannot find name 'PostAuthApiResponse'.
  const loadSpaData = (payload: PostAuthApiResponse) => {
    const payLoadWhitelabelClient = utils.safeGet(payload, 'whiteLabelClient', '');
    // load partner theme if necessary
    if (payload && payLoadWhitelabelClient) {
      // set overriding client id based on payload.whiteLabelClient.
      // see api.ts api() which depends on window.clientId and window.isDwm
      window.clientId = payLoadWhitelabelClient;
      loadPartnerTheme(payLoadWhitelabelClient, localizedStringBundle, countryCode);
      dispatch(whiteLabelClient(payLoadWhitelabelClient));
    }

    if (payload && payload.accessToken) {
      setHeaderAccessToken(payload.accessToken); // used by fetchRest.ts and api.ts to call Member API auth-required endpoints
    }

    return dispatch(loginRefresh()).then((result: $TSFixMe) => {
      window.isDwm = utils.safeGet(result, 'user.primaryMember.plan.isDwm', false);
      if (result.type === 'LOGIN_FAILURE') {
        return result; // return immediately and let the caller decide how to handle login failure
      }

      // Seed the React Query cache from the login fetch so useMemberData reports
      // success (and serves data) without triggering a second /v1/members request.
      queryClient.setQueryData([QUERY_KEY_MEMBER_DATA], result.user);

      // Reset the guard flag so data can be fetched again
      hasFetchedDataRef.current = false;

      setState((prevState) => ({
        ...prevState,
        getMode: true,
        needLifeLockTermsConditions: payload && payload.needLifeLockTermsConditions,
        whiteLabelClient: payload && payload.whiteLabelClient,
        needsDSPOnboarding: utils.safeGet(result, 'user.primaryMember.needsDSPOnboarding', false),
        showQuebecReimbursementOptIn: utils.safeGet(
          result,
          'user.primaryMember.plan.showQuebecReimbursementOptIn',
          false
        ),
        fetchUserData: true,
      }));
      const userCountryCode = utils.safeGet(result, 'user.countryCode', '');
      void invalidatePrivacyMonitorQueries(queryClient, userCountryCode);
      // login was successful, continue loading data
      if (window.isDwm) {
        fetchInboxAlerts();
      } else {
        // currently, mobile apps only load the SPA in a webview to display the alert detail page.
        // the API calls in this method are not necessary in that scenario.
        if (result.isMobileApp) {
          return null;
        }
      }
      // for some partner whiteLabelClients, config is maintained within dsp app based on userCountryCode
      if (isWhiteLabelClientCustomConfig(payLoadWhitelabelClient)) {
        overrideConfigs(getCustomPartnerConfig(payLoadWhitelabelClient, userCountryCode));
      }
      return result;
    });
  };

  /**
   * Sends a message to window parent indicating the new location.
   * Usually done in response to user navigating inside iframe and the need to keep the
   * window parent's location in sync to support bookmarking.
   * @param location
   */
  const updateParentLocation = (location: Location) => {
    const pathArr = location.pathname.split('/');
    // Keep modal open when pagination in alerts model or when modal is shown via app container
    const keepModalOpen = (pathArr[1] === 'alerts' && pathArr[3] === 'page') || state.showingModal;
    const openModalType = utils.safeGet(propModal, 'modalType', null);

    // Close modal on browser location changes
    if (!_isEmpty(openModalType) && !keepModalOpen) {
      utils.closeModal({propModal, dispatch});
    }
    const dynamicData = {privacyLicense};
    const title = utils.getPageTitle(pathArr[1], localizedStringBundle, dynamicData);

    // Preserve query params (HashRouter deep links like `#/transactions?addAccount=true`)
    // so parent URL sync doesn't strip them.
    const hashWithSearch = `${location.pathname}${location.search || ''}`;

    // `hash` + `title` are used by iframeListeners / itpsSpaLoader. `pathname` preserves the legacy
    // wire shape (pathname-only string) for older hosts that read it alongside the object payload.
    utils.sendMessage(
      {
        type: 'LOCATION_CHANGED',
        payload: {hash: hashWithSearch, title, pathname: location.pathname},
      },
      window.REACT_APP_PARENT_HOST
    );
    utils.resizeIframe();
  };

  const {lastParentDrivenHashRef} = useParentLocationSync({
    location,
    navigationType,
    updateParentLocation,
  });

  /**
   * Updates the hash portion of this window's location.
   * Usually done in response to receiving a message from window parent due to user
   * navigating with back and forward buttons on browser.
   * @param {string} locationHash - the part of the URL after the "#" character
   * @param {string} queryString - query string portion of URL path e.g. "?x=1&y=2"
   */
  const updateLocationHash = (locationHash: string, queryString: string) => {
    let nextPath = locationHash || '';
    let nextQuery = queryString || '';
    let queryFromDecodedHash = '';

    // Parent can send encoded hash paths for deep links (e.g.
    // `/card-shield/merchant/id%3Fdomain=...%26variant=...`).
    // Always decode first so query params stay in location.search, not in :merchantId.
    const decodedHashPath = decodeHashPathForRouting(nextPath);
    const queryIndex = decodedHashPath.indexOf('?');
    if (queryIndex >= 0) {
      nextPath = normalizeHashPathnameForRouting(decodedHashPath.slice(0, queryIndex));
      queryFromDecodedHash = decodedHashPath.slice(queryIndex);
    } else {
      nextPath = normalizeHashPathnameForRouting(decodedHashPath);
    }

    if (!queryFromDecodedHash && nextPath.includes('?')) {
      const queryIndex = nextPath.indexOf('?');
      queryFromDecodedHash = nextPath.slice(queryIndex);
      nextPath = nextPath.slice(0, queryIndex);
    }

    if (queryFromDecodedHash) {
      // For deep links, prefer the query carried inside locationHash over
      // parent-level `?redirectUrl=...` queryString.
      nextQuery = queryFromDecodedHash;
    }

    if (isSocialMediaOAuthRedirectStubPathname(nextPath)) {
      const resolvedStubTarget = resolveSmmOAuthStubNavigateTarget(nextQuery);
      if (resolvedStubTarget) {
        const resolvedQueryIndex = resolvedStubTarget.indexOf('?');
        nextPath = resolvedQueryIndex >= 0 ? resolvedStubTarget.slice(0, resolvedQueryIndex) : resolvedStubTarget;
        nextQuery = resolvedQueryIndex >= 0 ? resolvedStubTarget.slice(resolvedQueryIndex) : '';
      }
    }

    const targetHash = `${nextPath}${nextQuery}`;
    const currentHash = `${location?.pathname || ''}${location?.search || ''}`;
    if (targetHash && currentHash !== targetHash) {
      // Skip one echo of LOCATION_CHANGED for this parent-driven navigate (see useParentLocationSync).
      lastParentDrivenHashRef.current = targetHash;
      navigate(targetHash, {state: {updateParent: false}});
    }
  };

  /**
   * Sizes the open modal shell from the parent DWM host's `GET_WINDOW_SPEC` payload.
   *
   * The parent reports iframe scroll position and chrome dimensions (header, footer, viewport
   * height) so modal content can align with the visible band below the fixed header. This is
   * the default layout path: symmetric `left` / `right` margins with scroll-coupled `top` and
   * `maxHeight` (full-bleed on small screens).
   *
   * Many modals register their own `modalSizeSpec.content` before or during open instead:
   * - **Centered-width** — `left: '50%'` + `translateX(-50%)` and an explicit `maxWidth`
   * - **Viewport-centered** — `left: '50%'` + `translate(-50%, -50%)` with a capped height
   * - **Fixed inset** — `position: fixed` + `margin: auto` narrow cards
   *
   * When the Redux spec already matches one of those patterns, this handler only refreshes
   * scroll-dependent fields (`top`, `maxHeight`, `scrollTop`, …) and does **not** replace
   * horizontal sizing. Overwriting a narrow centered shell with iframe margins widens it to
   * nearly full viewport for a frame and causes a visible resize flicker.
   *
   * When no modal is open, this handler returns without dispatching. Writing the generic
   * margin layout into Redux while idle left a stale near-full-width spec that the next open
   * could paint before its opener registered the real shell dimensions.
   *
   * @param data - Parent window spec (`scrollTop`, `offsetTop`, `windowHeight`, `windowWidth`,
   *   `headerHeight`, `footerHeight`, `scrollHeight`, …).
   */
  const updateModalSize = (data: GetWindowSpecPayload) => {
    const activeModal = propModalRef.current;
    const openModalType = activeModal?.modalType ?? null;

    const xSpacing = 135;
    const ySpacing = 20;
    let top;
    let bottom;
    let left;
    let right;
    let maxHeight;

    let calculatedTop = data?.scrollTop - data?.offsetTop || 0;
    calculatedTop = Math.max(0, calculatedTop);

    const isSmallScreen =
      data.windowHeight < globalConstants.SMALL_SCREEN_WIDTH || data.windowWidth < globalConstants.MEDIUM_SCREEN_WIDTH;

    if (isSmallScreen) {
      // Occupy full width/height
      bottom = '0px';
      left = '0px';
      right = '0px';
      top = calculatedTop + 'px';
      maxHeight = data.windowHeight - data.headerHeight + 'px';
    } else {
      top = calculatedTop + ySpacing + 'px';
      bottom = 'auto';
      left = xSpacing + 'px';
      right = xSpacing + 'px';
      if (data.scrollTop + data.windowHeight + data.footerHeight < data.scrollHeight) {
        // When norton footer is not visible
        maxHeight = data.windowHeight - data.headerHeight - 2 * ySpacing + 'px';
      } else {
        // When norton footer is visible
        maxHeight = data.windowHeight - data.headerHeight - data.footerHeight - 2 * ySpacing + 'px';
      }
    }

    const existingContent = activeModal?.modalSizeSpec?.content as Record<string, string> | undefined;

    /**
     * Centered-width modals set `modalSizeSpec.content` with `left: 50%` + `transform: translateX(-50%)` (and their own
     * `maxWidth`, e.g. `50rem`) via `setModalSizeSpec` before open. `GET_WINDOW_SPEC` must not replace that with iframe
     * `left`/`right` margins alone — that widens the shell to near-viewport while inner UI stays narrow.
     */
    const usesCenteredWidthLayout =
      !isSmallScreen &&
      Boolean(
        existingContent &&
          existingContent.left === '50%' &&
          typeof existingContent.transform === 'string' &&
          existingContent.transform.includes('translateX')
      );

    const usesViewportCenteredLayout =
      !isSmallScreen && isViewportCenteredModalContent(existingContent as CenteredModalSizeSpecStyle);

    if (usesViewportCenteredLayout) {
      dispatch(
        setModalSizeSpec({
          ...(activeModal?.modalSizeSpec ?? {}),
          content: buildScrollAwareViewportCenteredContent(existingContent as CenteredModalSizeSpecStyle, data),
        })
      );
      return;
    }

    if (usesCenteredWidthLayout) {
      dispatch(
        setModalSizeSpec({
          ...(activeModal?.modalSizeSpec ?? {}),
          content: {
            ...(existingContent || {}),
            top,
            bottom,
            maxHeight,
            scrollTop: data.scrollTop,
          },
        })
      );
      return;
    }

    /**
     * `position: fixed` + `inset: 0` + `margin: auto` dialogs (narrow card centered in the viewport). Same resize rule
     * as above: do not replace with iframe `left`/`right` margin layout.
     */
    const usesFixedInsetCenteredDialog =
      !isSmallScreen &&
      Boolean(
        existingContent &&
          String(existingContent.position) === 'fixed' &&
          String(existingContent.margin ?? '').includes('auto')
      );

    if (usesFixedInsetCenteredDialog) {
      dispatch(
        setModalSizeSpec({
          ...(activeModal?.modalSizeSpec ?? {}),
          content: {
            ...(existingContent || {}),
            maxHeight,
            scrollTop: data.scrollTop,
          },
        })
      );
      return;
    }

    // Do not persist the generic margin layout while no modal is open (see JSDoc above).
    if (!openModalType) {
      return;
    }

    dispatch(
      setModalSizeSpec({
        content: {
          top,
          bottom,
          left,
          right,
          maxHeight,
          scrollTop: data.scrollTop,
        },
      })
    );
  };

  /**
   * Repositions the body based on the provided data.
   * @param data - The payload containing window specifications.
   */
  const repositionBody = (data: GetWindowSpecPayload) => {
    if (/iP(hone|od|ad)/.test(navigator.platform)) {
      document.getElementById('csp-app-container')?.scrollTo(0, data.scrollTop);
    }
  };

  /**
   * Set iframe size and position data from the window size specifications
   * To be used when calculating "absolute" positioned elements based on user's initial window prior to any scroll event being fired
   * @param payload
   */
  const setAppSizeScrollData = (payload: GetWindowSpecPayload) => {
    if (payload && !_isEmpty(payload)) {
      const {scrollTop, windowHeight, offsetTop, headerHeight} = payload;
      dispatch(
        setWindowScrolledEvent({
          scrollTop,
          windowHeight,
          offsetTop,
          headerHeight,
        })
      );
    }
  };

  /**
   * Sets the redux store scroll object using the data from input payload from a scroll event
   * @param payload
   */
  const setScrollData = (payload: ScrollEventPayload) => {
    dispatch(setWindowScrolledEvent(payload));
  };

  /**
   * Sets the height of the app container.
   * @param payload - The payload containing the window specifications.
   */
  const setAppContainerHeight = (payload: GetWindowSpecPayload) => {
    if (payload) {
      let headerHeight = payload.headerHeight || 0;
      let footerHeight = payload.footerHeight || 0;
      const windowHeight = payload.windowHeight || 0;
      // Below condition was added to get the best height when the browser is in/not a full screen mode.
      // However, in mobile mode we constrain the app container to a scrollable viewport height.
      // In some embedded/iframe contexts `screen.availHeight` can be larger than the usable viewport,
      // which inflates min-height and causes parent iframe resizing issues.
      const totalHeight =
        useDspUiRef.current && isMobileModeRef.current ? windowHeight : Math.max(screen.availHeight, windowHeight);
      // Adobe banner addition/removal is dynamic and this is needed to calculate the right height for the app
      // Any future dynamic banner/element addition to the app on any page, logic needs to be added to calculate the height
      const adobeElement = document.getElementById('adobeBanner') as HTMLElement;
      let adobeBannerHeight = adobeElement ? adobeElement.offsetHeight : 0;

      // when banner is turned on in full screen vs not full screen browser
      if (windowHeight === screen.availHeight) {
        adobeBannerHeight = 0;
      }

      // when height of the browser is less than iframe max-height
      if (windowHeight < globalConstants.IFRAME_MAX_HEIGHT) {
        headerHeight = 0;
        footerHeight = 0;
      }

      // when footer is longer(incase of partners), zooming out of csp app is causing display issues
      if (footerHeight > globalConstants.IFRAME_MAX_FOOTER_HEIGHT) {
        footerHeight = globalConstants.IFRAME_MAX_FOOTER_HEIGHT;
      }
      const appHeight = totalHeight - (headerHeight + footerHeight + adobeBannerHeight);
      if (appContainerRef.current) {
        appContainerRef.current.style.setProperty('min-height', `${appHeight}px`, 'important');
        utils.resizeIframe();
      }
    }
  };

  /**
   * Returns header based on window mode
   * @returns {JSX.Element} HeaderDwa if primary member has a DWM plan.  HeaderDsp if DSP plan. Null otherwise.
   */
  const getHeader = () => {
    const useDspUi = shouldUseDspUi(auth);
    const useDwmUi = shouldUseDwmUi(auth);
    const errorStatus = utils.safeGet(auth, 'loginError.result.status', null);
    const productFeatures = utils.safeGet(auth, 'user.primaryMember.plan.productFeatures');
    const marketingDetails = utils.safeGet(auth, 'user.primaryMember.plan.marketingDetails');
    const whiteLabelClient = utils.safeGet(auth, 'whiteLabelClient', '');

    if (errorStatus === 429) {
      return null;
    }

    if (useDwmUi) {
      return (
        <HeaderDwa
          productFeatures={productFeatures}
          whiteLabelClient={whiteLabelClient}
          marketingDetails={marketingDetails}
        />
      );
    } else if (useDspUi) {
      return (
        <HeaderDsp
          isMobileApp={auth.isMobileApp}
          productFeatures={productFeatures}
          marketingDetails={marketingDetails}
        />
      );
    }
    return null;
  };

  /**
   * Shows the loader.
   */
  const showLoader = () => {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center bg-background p-20">
        <Loader />
      </div>
    );
  };

  /**
   * Handles the redirect.
   * @param {any} props - The props for the redirect.
   */
  const handleRedirect = () => {
    const search = location?.search || '';
    const params = new URLSearchParams(search);

    // Capture analytics landing params (inid, om_em_cid) before the redirect
    // discards them.  Adobe Analytics' doPlugins reads these via s.getQueryParam
    // which checks s.pageURL; setPageNameForAnalytics will consume this value.
    const inid = params.get('inid');
    const omEmCid = params.get('om_em_cid');
    if (inid || omEmCid) {
      const qs = new URLSearchParams();
      if (inid) qs.set('inid', inid);
      if (omEmCid) qs.set('om_em_cid', omEmCid);
      window._analyticsLandingParams = qs.toString();
    }

    // Check for both case variations: redirectUrl and redirecturl
    const redirectUrl = params?.get('redirectUrl') || params?.get('redirecturl');
    if (redirectUrl) {
      const urlQueryString = utils.parseQueryString(search);
      if (!_isEmpty(urlQueryString)) {
        dispatch(setModalQuery(urlQueryString));
      }
      // Merges sibling SMM params onto redirectUrl; null only when redirectUrl is absent from search (not this branch).
      const target = buildSmmOAuthStubNavigateTarget(search) ?? decodeURIComponent(redirectUrl);
      return <Navigate to={target} />;
    }

    const hashSearch = extractSearchFromWindowHash();
    const smmReturnTarget =
      isOAuthStubInWindowHash() && hashSearch ? resolveSmmOAuthStubNavigateTarget(hashSearch) : null;
    if (smmReturnTarget) {
      const targetSearch =
        smmReturnTarget.indexOf('?') >= 0 ? smmReturnTarget.slice(smmReturnTarget.indexOf('?')) : search;
      const urlQueryString = utils.parseQueryString(targetSearch);
      if (!_isEmpty(urlQueryString)) {
        dispatch(setModalQuery(urlQueryString));
      }
      return <Navigate to={smmReturnTarget} />;
    }

    const smmCancelTarget = resolveSmmOAuthCancelReturnTarget();
    if (smmCancelTarget) {
      clearSmmPendingOAuthReturn();
      return <Navigate to={smmCancelTarget} />;
    }

    return <Navigate to={'/dashboard'} />;
  };

  /**
   * Returns DWM or DSP specific routes
   * @return {*}
   */
  const getMode = () => {
    const useDwmUi = shouldUseDwmUi(auth);
    const whiteLabelClient = utils.safeGet(auth, 'whiteLabelClient', '');
    if (useDwmUi) {
      return (
        <Routes>
          <Route path="/memberchat" element={<MemberChatShell />} />
          <Route path="/*" element={<DarkWebLanding />} />
        </Routes>
      );
    } else {
      return (
        <Routes>
          <Route path="/" Component={handleRedirect} />
          <Route path="/alerts" element={<AlertListComponent />} />
          <Route path="/alerts/:id" element={<AlertListComponent />} />
          <Route path="/alerts/:id/page/:pagenum" element={<AlertListComponent />} />
          <Route path="/alertPreferences" element={<AlertPreferences hideLabel={true} />} />
          <Route
            path="/externalredirect/creditoffers"
            element={
              <CreditOffersRedirect
                redirectUrl={encodeURIComponent(utils.getCtaUrlFromHash())}
                vendorName={new URLSearchParams(window.location.hash.split('?')[1] || '').get('vendor')}
              />
            }
          />
          <Route path="/advanceDispositionFragment" element={<AlertsLanding />} />
          <Route path="/bureauredirect" element={<BureauRedirect />} />
          <Route
            path="/credit"
            element={
              hasAccess('/credit') ? (
                <Credit />
              ) : (
                // GUTS-6771: deep-link users who lack a credit subscription should land on
                // /dashboard, not /error, so there is no indication of why the link failed.
                <Navigate to={hasCreditAccountDeepLink(location.search) ? '/dashboard' : '/error'} />
              )
            }
          />

          <Route path="/credit-au" element={hasAccess('/credit-au') ? <CreditAU /> : <Navigate to={'/error'} />} />
          <Route
            path="/credit/:tab"
            element={
              hasAccess('/credit/:tab') ? (
                <Credit />
              ) : (
                <Navigate to={hasCreditAccountDeepLink(location.search) ? '/dashboard' : '/error'} />
              )
            }
          />
          <Route
            path="/credit/:tab/:gaCategory"
            element={
              hasAccess('/credit/:tab') ? (
                <Credit />
              ) : (
                <Navigate to={hasCreditAccountDeepLink(location.search) ? '/dashboard' : '/error'} />
              )
            }
          />
          <Route
            path="/credit-monitor/:gaCategory"
            element={hasAccess('/credit-monitor') ? <CreditMonitor /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/credit-monitor"
            element={hasAccess('/credit-monitor') ? <CreditMonitor /> : <Navigate to={'/error'} />}
          />
          <Route path="/disposition/alertConfirmedValidFragment" element={<AlertsLanding />} />
          <Route path="/disposition/alertConfirmedInvalidFragment" element={<AlertsLanding />} />
          <Route path="/disposition/alertConfirmedGrayInvalidFragment" element={<AlertsLanding />} />
          <Route path="/disposition/alertConfirmedRegularInvalidFragment" element={<AlertsLanding />} />
          <Route path="/disposition/alertDispositionedFragment" element={<AlertsLanding />} />
          <Route path="/disposition/custodianAlertConfirmedValidFragment" element={<AlertsLanding />} />
          <Route path="/disposition/custodianAlertConfirmedInValidFragment" element={<AlertsLanding />} />
          <Route path="/disposition/error" element={<AlertsLanding />} />
          <Route path="/error" element={<ErrorPage />} />
          <Route path="/plandetails" element={<PlanDetails />} />
          <Route path="/logout" element={<LoggedOut />} />
          <Route path="/dashboard" element={hasAccess('/dashboard') ? <Dashboard /> : <Navigate to={'/alerts'} />} />
          <Route
            path="/hometitle"
            element={hasAccess('/hometitle') ? <HomeTitleMonitoring /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/hometitle/verify"
            element={hasAccess('/hometitle') ? <VerifyHomeTitle /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/privacyadvisor"
            element={hasAccess('/privacyadvisor') ? <PrivacyAdvisor /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/privacymonitor/*"
            element={hasAccess('/privacymonitor') ? <PrivacyMonitor /> : <Navigate to={'/error'} />}
          />

          <Route path="/locks/*" element={hasAccess('/locks') ? <ItpsLocksAndFreeze /> : <Navigate to={'/error'} />} />
          <Route path="/memberchat" element={<MemberChatShell />} />
          <Route
            path="/genie"
            element={
              hasAccess('/genie') ? (
                <Genie
                  gaEventCategory="App"
                  showOpen={true}
                  modifyIntentTopics={(topics) => topics.filter((topic) => topic.intent === 'cyber_safety')}
                />
              ) : (
                <Navigate to={'/error'} />
              )
            }
          />
          {utils.hasMFeed(whiteLabelClient) && utils.safeGet(auth, 'user.primaryMember.showContentful', false) && (
            <Route path="/discover" element={<Discover />} />
          )}
          <Route
            path="/monitoring/:section?/:accountId?"
            element={hasAccess('/monitoring/:section?/:accountId?') ? <MonitoringInfo /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/card-shield"
            element={hasAccess('/card-shield') ? <CardShieldLanding /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/card-shield/past-removals"
            element={
              hasAccess('/card-shield/past-removals') ? <CardShieldPastRemovalsPage /> : <Navigate to={'/error'} />
            }
          />
          <Route
            path="/card-shield/trusted"
            element={
              hasAccess('/card-shield/trusted') ? <CardShieldTrustedMerchantsPage /> : <Navigate to={'/error'} />
            }
          />
          <Route
            path="/card-shield/merchant/:merchantId"
            element={hasAccess('/card-shield') ? <CardShieldMerchantDetailPage /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/card-shield/entry"
            element={hasAccess('/card-shield') ? <CardShieldEntryResolver /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/card-shield-mvp"
            element={hasAccess('/card-shield') ? <Navigate to="/card-shield" replace /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/card-shield-legacy"
            element={hasAccess('/card-shield') ? <Navigate to="/card-shield" replace /> : <Navigate to={'/error'} />}
          />
          <Route path="/pageNotAvailable" element={<AlertsLanding />} />
          <Route path="/restoration" element={<RestorationCases />} />
          <Route path="/restoration/:id" element={<CaseDetails />} />
          <Route
            path="/estate-planning/documents"
            element={
              hasAccess('/estate-planning/documents') ? <EstatePlanningDocumentsPage /> : <Navigate to={'/error'} />
            }
          />
          <Route
            path="/estate-planning/notarize"
            element={
              hasAccess('/estate-planning/notarize') ? <EstatePlanningNotarizePage /> : <Navigate to={'/error'} />
            }
          />
          <Route
            path="/estate-planning/fund-trust"
            element={
              hasAccess('/estate-planning/fund-trust') ? <EstatePlanningFundTrustPage /> : <Navigate to={'/error'} />
            }
          />
          <Route
            path="/estate-planning"
            element={hasAccess('/estate-planning') ? <EstatePlanning /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/scam-protection"
            element={hasAccess('/scam-protection') ? <ScamProtection /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/scam-protection/financial-details"
            element={hasAccess('/scam-protection') ? <ScamProtectionFinancialDetails /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/scam-protection/recovery"
            element={hasAccess('/scam-protection') ? <ScamProtectionRecovery /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/scam-protection/recovery-plan"
            element={hasAccess('/scam-protection') ? <ScamProtectionRecoveryPlan /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/scam-protection/claim"
            element={hasAccess('/scam-protection') ? <ScamProtectionClaim /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/social-media-monitoring/add-account-success"
            element={
              hasAccess('/social-media-monitoring/add-account-success') ? (
                <SocialMediaMonitoring accountId={accountId} isPrimary={true} primaryMemberAccountId={accountId} />
              ) : (
                <Navigate to={'/error'} />
              )
            }
          />
          <Route
            path="/social-media-monitoring/add-account-error"
            element={
              hasAccess('/social-media-monitoring/add-account-error') ? (
                <SocialMediaMonitoring accountId={accountId} isPrimary={true} primaryMemberAccountId={accountId} />
              ) : (
                <Navigate to={'/error'} />
              )
            }
          />
          <Route
            path="/transactions"
            element={
              hasAccess('/transactions') ? (
                isTxmJpEnabled ? (
                  <Transactions />
                ) : (
                  <TransactionsUnified />
                )
              ) : (
                <Navigate to={'/error'} />
              )
            }
          />
          <Route
            path="/transactions/accounts"
            element={
              hasAccess('/transactions') ? (
                isTxmJpEnabled ? (
                  <Transactions />
                ) : (
                  <TransactionsUnified />
                )
              ) : (
                <Navigate to={'/error'} />
              )
            }
          />
          <Route
            path="/transactions/all"
            element={
              hasAccess('/transactions/all') ? (
                isTxmJpEnabled ? (
                  <Transactions />
                ) : (
                  <TransactionsUnified />
                )
              ) : (
                <Navigate to={'/error'} />
              )
            }
          />
          <Route
            path="/transactions/all/search"
            element={
              hasAccess('/transactions/all/search') ? (
                isTxmJpEnabled ? (
                  <TransactionsSearch />
                ) : (
                  <TransactionsSearchUnified />
                )
              ) : (
                <Navigate to={'/error'} />
              )
            }
          />
          <Route
            path="/transactions/recurring/all"
            element={
              hasAccess('/transactions/recurring/all') ? (
                isTxmJpEnabled ? (
                  <Transactions />
                ) : (
                  <TransactionsUnified />
                )
              ) : (
                <Navigate to={'/error'} />
              )
            }
          />
          <Route
            path="/transactions/recurring/details/:recurringTagId/:returnPath?"
            element={<RecurringTransactionDetailView />}
          />
          <Route
            path="/txm-transactions/txmJp-add-account-error"
            element={
              hasAccess('/txm-transactions/txmJp-add-account-error', false) ? (
                <Transactions />
              ) : (
                <Navigate to={'/error'} />
              )
            }
          />
          <Route
            path="/monitoring/add-account-error"
            element={
              hasAccess('/monitoring/add-account-error', true, false) ? <MonitoringInfo /> : <Navigate to={'/error'} />
            }
          />
          <Route
            path="/txm-monitoring/txmJp-add-account-error"
            element={
              hasAccess('/txm-monitoring/txmJp-add-account-error', false, false) ? (
                <MonitoringInfo />
              ) : (
                <Navigate to={'/error'} />
              )
            }
          />
          <Route path="/tuSummaryOfRights" element={<TuSummaryOfRights />} />
          <Route path="/summaryOfRights" element={<SummaryOfRights />} />
          <Route path="/appObserver" element={<AppObserver />} />
          <Route
            path="/scam-verification"
            element={hasAccess('/scam-verification') ? <ScamVerification /> : <Navigate to={'/error'} />}
          />
          <Route path="/scam-verification/:id" element={<ScamVerificationCaseDetails />} />
          <Route path="/appObserver" element={<AppObserver />} />
          <Route
            path="/marketplace"
            element={hasAccess('/marketplace') ? <Marketplace tabView="credit" /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/marketplace/credit"
            element={hasAccess('/marketplace/credit') ? <Marketplace tabView="credit" /> : <Navigate to={'/error'} />}
          />
          <Route
            path="/marketplace/savings"
            element={hasAccess('/marketplace/savings') ? <Marketplace tabView="savings" /> : <Navigate to={'/error'} />}
          />
          <Route element={<ErrorPage />} />
        </Routes>
      );
    }
  };

  if (showFcraColp && !fcraState.isModalShown) {
    return (
      <FcraModal
        isAgent={isAgent}
        showErrorMsg={!_isEmpty(fcraState.error)}
        localizedStringBundle={localizedStringBundle}
        showFcraColp={showFcraColp}
      />
    );
  } else if (state.loggedOut) {
    return <LoggedOut />;
  } else {
    const isLLMobileApp = utils.safeGet(auth, 'isMobileApp', false);
    const whiteLabelClientId = utils.safeGet(auth, 'whiteLabelClient', '');
    const isInFlowFinancialSurface = isMobileInFlowFinancialSurfacePath(location.pathname);
    // Reserve top space for fixed mobile chrome (e.g. Moneylion). In-flow headers (default DSP) do not need it;
    // omit on Card Shield + Financial Monitoring (/transactions) so content sits flush under the menu bar.
    const applyMobileShellTopPadding =
      isMobileMode &&
      useDspUi &&
      !isLLMobileApp &&
      (!isInFlowFinancialSurface || hasFixedMobileMenu(whiteLabelClientId));
    return (
      <IntlProvider>
        <Suspense fallback={<></>}>
          <ModalRoot />
        </Suspense>
        <StrictMode>
          <ErrorBoundary errorComponent={<ErrorPage />}>
            <div className={appContainerClasses} data-testid="app-container" ref={appContainerRef}>
              {auth?.user && getHeader()}
              <div className={classNames('w-full bg-background', applyMobileShellTopPadding && 'pt-12')}>
                <div className={appBackgroundClasses}>
                  <div className={appClasses}>
                    <div className="hidden">{process.env.REACT_APP_LABEL}</div>
                    <iframe title="authIframeTitle" id="authIframe" src="" className="hidden" />
                    <div
                      className={classNames('overflow-auto', auth?.isLoading === false ? 'h-full' : 'h-screen')}
                      id="csp-app-container"
                    >
                      <Suspense fallback={showLoader()}>
                        {location.pathname !== '/dashboard' && <AdobeBannerList />}
                        {state.getMode || isPublicRoute ? getMode() : showLoader()}
                      </Suspense>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ErrorBoundary>
        </StrictMode>
      </IntlProvider>
    );
  }
};

export default App;
